// ================================================
// CLARITY REPORTING — LOGIN PAGE
// ================================================

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export function LoginPage() {
  const { login }   = useAuth();
  const navigate    = useNavigate();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      fontFamily: 'IBM Plex Sans, sans-serif'
    }}>
      {/* Left panel */}
      <div style={{
        width: 440,
        background: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px 48px',
        flexShrink: 0
      }}>
        <div>
          <div style={{
            fontSize: 13, fontWeight: 700, letterSpacing: 3,
            textTransform: 'uppercase', color: '#fff', lineHeight: 1.4
          }}>
            Clarity
            <span style={{
              display: 'block', fontWeight: 300, fontSize: 10,
              letterSpacing: 2, opacity: 0.6, marginTop: 2
            }}>Reporting</span>
          </div>
        </div>

        <div>
          <blockquote style={{
            margin: 0, color: 'rgba(255,255,255,0.6)',
            fontSize: 15, lineHeight: 1.7, fontStyle: 'italic'
          }}>
            "Independent SEO data. No spin. No agenda. Just the facts."
          </blockquote>
        </div>

        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', letterSpacing: 1 }}>
          © {new Date().getFullYear()} Clarity Reporting
        </div>
      </div>

      {/* Right panel */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#fafafa', padding: 40
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <h2 style={{
            fontSize: 20, fontWeight: 700, letterSpacing: 1,
            textTransform: 'uppercase', marginBottom: 8
          }}>
            Sign In
          </h2>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 32 }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: '#1a1a1a', fontWeight: 600 }}>
              Create one
            </Link>
          </p>

          {error && (
            <div className="alert alert-error">{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: 12, color: '#888' }}>
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ================================================
// REGISTER PAGE
// ================================================

export function RegisterPage() {
  const { register } = useAuth();
  const navigate     = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '',
    password: '', account_type: 'site_owner'
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  function handleChange(e) {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(formData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const plans = [
    { value: 'site_owner', label: 'Site Owner', price: '$20/mo', desc: '1 website + 3 competitors' },
    { value: 'agency',     label: 'Agency',     price: '$99/mo', desc: 'Up to 20 clients + white label' },
    { value: 'agency_pro', label: 'Agency Pro', price: '$199/mo', desc: 'Unlimited clients + white label' }
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {/* Left panel */}
      <div style={{
        width: 440, background: '#1a1a1a', display: 'flex',
        flexDirection: 'column', justifyContent: 'space-between',
        padding: '48px', flexShrink: 0
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#fff', lineHeight: 1.4 }}>
          Clarity<span style={{ display: 'block', fontWeight: 300, fontSize: 10, letterSpacing: 2, opacity: 0.6, marginTop: 2 }}>Reporting</span>
        </div>
        <div>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.7, marginBottom: 32 }}>
            Independent SEO data for business owners who want the truth, not a curated narrative.
          </p>
          {plans.map(p => (
            <div key={p.value} style={{ marginBottom: 20, paddingLeft: 16, borderLeft: formData.account_type === p.value ? '2px solid #fff' : '2px solid rgba(255,255,255,0.1)' }}>
              <div style={{ color: formData.account_type === p.value ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: 13 }}>
                {p.label} — {p.price}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>{p.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', letterSpacing: 1 }}>© {new Date().getFullYear()} Clarity Reporting</div>
      </div>

      {/* Right panel */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', padding: 40 }}>
        <div style={{ width: '100%', maxWidth: 480 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            Create Account
          </h2>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 32 }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#1a1a1a', fontWeight: 600 }}>Sign in</Link>
          </p>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Plan selection */}
            <div className="form-group">
              <label className="form-label">Select Plan</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {plans.map(p => (
                  <label key={p.value} style={{
                    display: 'block', padding: '12px', border: formData.account_type === p.value
                      ? '2px solid #1a1a1a' : '1px solid #d0d0d0',
                    cursor: 'pointer', textAlign: 'center'
                  }}>
                    <input
                      type="radio" name="account_type" value={p.value}
                      checked={formData.account_type === p.value}
                      onChange={handleChange}
                      style={{ display: 'none' }}
                    />
                    <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: 0.5 }}>{p.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, margin: '4px 0' }}>{p.price}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{p.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input className="form-input" name="first_name" value={formData.first_name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="form-input" name="last_name" value={formData.last_name} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input type="email" className="form-input" name="email" value={formData.email} onChange={handleChange} required />
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Password</label>
              <input type="password" className="form-input" name="password" value={formData.password} onChange={handleChange} required minLength={8} />
              <div className="form-hint">Minimum 8 characters</div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
              {loading ? 'Creating account...' : 'Create Account — Continue to Billing'}
            </button>

            <p style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: 16, lineHeight: 1.5 }}>
              By creating an account you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
